import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import AnimatedBackground from '@/components/AnimatedBackground';
import { ArrowLeft, Calendar, Tag, ArrowUpRight, Bookmark, CheckCircle2 } from 'lucide-react';
import { papers } from '@/data/papers';

const PaperDetails = () => {
  const { paperId } = useParams();
  const navigate = useNavigate();

  const paper = papers.find(p => p.id === paperId);

  if (!paper) {
    return (
      <div className="min-h-screen relative">
        <AnimatedBackground />
        <Navigation />
        <main className="pt-24 pb-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Paper Not Found
            </h1>
            <button
              onClick={() => navigate('/papershelf')}
              className="text-green-600 dark:text-green-400 hover:underline"
            >
              Return to Papershelf
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <Navigation />
      
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/papershelf')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Papershelf
          </button>

          {/* Paper Header */}
          <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {paper.title}
                </h1>
                <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400 mb-4">
                  <span>{paper.authors}</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {paper.date}
                  </span>
                  <span>•</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium
                    ${paper.status === 'Currently Reading' 
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : paper.status === 'Completed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                    }`}
                  >
                    {paper.status}
                  </span>
                </div>
              </div>
              <a
                href={paper.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
              >
                Read Paper
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {paper.summary}
            </p>

            {paper.status === "Currently Reading" && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>Reading Progress</span>
                  <span>{paper.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${paper.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Key Learnings */}
          <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Key Learnings
            </h2>
            <ul className="space-y-3">
              {paper.keyLearnings.map((learning, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                  <span className="text-green-500 mt-1">•</span>
                  <span>{learning}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Personal Notes */}
          <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Personal Notes
            </h2>
            <p className="text-gray-700 dark:text-gray-300 italic">
              {paper.notes}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaperDetails; 